import { USER_PREFERENCES_LEGACY_KEYS } from '@common/enums';
import type { GrStatCardTone } from '@components/cards';
import { DASHBOARD_CHART_SOURCE_INDEX } from '../data/dashboard-catalog.data';
import {
  DEFAULT_DASHBOARD_LAYOUT,
  DEFAULT_DATE_RANGE,
  DEFAULT_ORGANIZATIONS,
  DEFAULT_SCOPE,
  DEFAULT_USER_PREFERENCES,
} from '../data/user-preferences.data';
import type {
  ChartBuilderChartFamily,
  ChartBuilderConfig,
  ChartBuilderSeriesDef,
  DashboardCanvasLayout,
  DashboardCardConfig,
  DashboardCardItem,
  DashboardChartColorScheme,
  DashboardChartDisplayOptions,
  DashboardChartGranularity,
  DashboardChartItem,
  DashboardChartLegendPosition,
  DashboardChartSortOrder,
  DashboardChartSourceKey,
  DashboardChartType,
  DashboardCustomChartConfig,
  DashboardCustomChartSeriesDef,
  DashboardCustomMetricDefinition,
  DashboardMetricItem,
  DashboardPreset,
  DashboardTableItem,
  DashboardTextItem,
  DashboardWidget,
  DateRangePreference,
  DateRangePresetId,
  OrganizationsPreference,
  PersistedReport,
  Report,
  ReportTab,
  ReportTag,
  ScopePreference,
  ScopeSelection,
  TableBuilderColumnDef,
  TableBuilderColumnGroup,
  TableBuilderConfig,
  TableBuilderExportConfig,
  TableBuilderExportFormat,
  ThemeMode,
  UserPreferences,
} from '../types';
import { ensureItemIds, isValidCardLayout, makeItemId } from './dashboard-chart.functions';

const VALID_TONES: ReadonlyArray<GrStatCardTone> = [
  'brand',
  'success',
  'danger',
  'warning',
  'neutral',
];

const VALID_PRESET_IDS: ReadonlyArray<DateRangePresetId> = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
  'last365',
  'custom',
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const VALID_CHART_TYPES: ReadonlyArray<DashboardChartType> = [
  'line',
  'bar',
  'bar-horizontal',
  'stacked-bar',
  'area',
  'donut',
  'rating-bars',
  'source-list',
];

type LegacyComboSecondaryType = 'line' | 'area' | 'bar' | 'bar-horizontal';
const LEGACY_COMBO_SECONDARY: ReadonlyArray<LegacyComboSecondaryType> = [
  'line',
  'area',
  'bar',
  'bar-horizontal',
];

const VALID_GRANULARITY: ReadonlyArray<DashboardChartGranularity> = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];
const VALID_LEGEND_POSITION: ReadonlyArray<DashboardChartLegendPosition> = [
  'top',
  'right',
  'bottom',
  'none',
];
const VALID_SORT_ORDER: ReadonlyArray<DashboardChartSortOrder> = [
  'value-desc',
  'value-asc',
  'name',
  'source-order',
];
const VALID_COLOR_SCHEME: ReadonlyArray<DashboardChartColorScheme> = [
  'brand',
  'multi',
  'monochrome',
  'sentiment',
];

export function toPersistedReport(r: Report): PersistedReport {
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    type: r.type,
    format: r.format,
    createdAt: r.createdAt instanceof Date ? r.createdAt.toISOString() : String(r.createdAt),
    updatedAt: r.updatedAt instanceof Date ? r.updatedAt.toISOString() : String(r.updatedAt),
    createdBy: r.createdBy,
    isPublic: r.isPublic,
    isFavourite: r.isFavourite ?? false,
    tags: r.tags ? r.tags.map((t) => ({ ...t })) : undefined,
    widgets: r.widgets.map(cloneWidget),
    tabs: r.tabs
      ? r.tabs.map((tab) => ({ ...tab, widgets: tab.widgets.map(cloneWidget) }))
      : undefined,
  };
}

export function cloneUserPreferences(prefs: UserPreferences): UserPreferences {
  return {
    theme: prefs.theme,
    dashboardLayout: prefs.dashboardLayout.map(cloneWidget),
    dashboardOrder: [...prefs.dashboardOrder],
    dateRange: { ...prefs.dateRange },
    reports: prefs.reports.map((r) => ({
      ...r,
      tags: r.tags ? r.tags.map((t) => ({ ...t })) : undefined,
      widgets: r.widgets.map(cloneWidget),
    })),
    tags: prefs.tags.map((t) => ({ ...t })),
    dashboardPresets: prefs.dashboardPresets.map((p) => ({
      ...p,
      config: { ...p.config, items: p.config.items.map(cloneItem) },
    })),
    scope: {
      selection: { ...prefs.scope.selection },
      favoriteViewIds: [...prefs.scope.favoriteViewIds],
      favoriteTagIds: [...prefs.scope.favoriteTagIds],
      favoriteAccountIds: [...prefs.scope.favoriteAccountIds],
    },
    organizations: { ...prefs.organizations },
    tableBuilderConfigs: prefs.tableBuilderConfigs.map(cloneTableBuilderConfig),
    chartBuilderConfigs: prefs.chartBuilderConfigs.map(cloneChartBuilderConfig),
    sidebarCollapsed: prefs.sidebarCollapsed,
    tableColumns: Object.fromEntries(
      Object.entries(prefs.tableColumns).map(([key, hidden]) => [key, [...hidden]]),
    ),
  };
}

function cloneChartBuilderConfig(c: ChartBuilderConfig): ChartBuilderConfig {
  return { ...c, series: c.series.map((s) => ({ ...s })) };
}

function cloneTableBuilderConfig(c: TableBuilderConfig): TableBuilderConfig {
  return {
    ...c,
    ...(c.columns ? { columns: c.columns.map((col) => ({ ...col })) } : {}),
    ...(c.columnGroups
      ? {
          columnGroups: c.columnGroups.map((g) => ({
            ...g,
            measures: g.measures.map((m) => ({ ...m })),
          })),
        }
      : {}),
    ...(c.exportConfig ? { exportConfig: { ...c.exportConfig } } : {}),
  };
}

export function reconcileUserPreferences(raw: unknown): UserPreferences {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    theme: reconcileTheme(obj['theme']),
    dashboardLayout: reconcileDashboardLayout(obj['dashboardLayout']),
    dashboardOrder: [...reconcileStringArray(obj['dashboardOrder'], [])],
    dateRange: reconcileDateRange(obj['dateRange']),
    scope: reconcileScope(obj['scope']),
    organizations: reconcileOrganizations(obj['organizations']),
    reports: reconcileReports(obj['reports']),
    tags: reconcileTags(obj['tags']),
    dashboardPresets: reconcilePresets(obj['dashboardPresets']),
    tableBuilderConfigs: reconcileTableBuilderConfigs(obj['tableBuilderConfigs']),
    chartBuilderConfigs: reconcileChartBuilderConfigs(obj['chartBuilderConfigs']),
    sidebarCollapsed: obj['sidebarCollapsed'] === true,
    tableColumns: reconcileTableColumns(obj['tableColumns']),
  };
}

const VALID_CHART_FAMILIES: ReadonlyArray<ChartBuilderChartFamily> = [
  'bar-mixed',
  'bar-horizontal',
  'area',
  'donut',
  'wordcloud',
];

function reconcileChartBuilderConfigs(raw: unknown): ChartBuilderConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const c = reconcileChartBuilderConfig(item);
    return c ? [c] : [];
  });
}

function reconcileChartBuilderConfig(raw: unknown): ChartBuilderConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
  const name = typeof obj['name'] === 'string' && obj['name'] ? obj['name'] : null;
  const xAxis = typeof obj['xAxis'] === 'string' && obj['xAxis'] ? obj['xAxis'] : null;
  if (!id || !name || !xAxis) return null;

  const rawSeries = obj['series'];
  const series: ChartBuilderSeriesDef[] = Array.isArray(rawSeries)
    ? rawSeries.filter(
        (s): s is ChartBuilderSeriesDef =>
          !!s &&
          typeof s === 'object' &&
          typeof (s as Record<string, unknown>)['measure'] === 'string' &&
          VALID_SERIES_TYPES.has((s as Record<string, unknown>)['chartType'] as string),
      )
    : [];
  if (!series.length) return null;

  const description =
    typeof obj['description'] === 'string' && obj['description'] ? obj['description'] : undefined;
  const chartFamily =
    typeof obj['chartFamily'] === 'string' &&
    VALID_CHART_FAMILIES.includes(obj['chartFamily'] as ChartBuilderChartFamily)
      ? (obj['chartFamily'] as ChartBuilderChartFamily)
      : undefined;
  const groupBy = typeof obj['groupBy'] === 'string' && obj['groupBy'] ? obj['groupBy'] : undefined;
  const groupMethodRaw = obj['groupMethod'];
  const groupMethod =
    groupMethodRaw === 'bars' || groupMethodRaw === 'stacked' ? groupMethodRaw : undefined;

  return {
    id,
    name,
    xAxis,
    series,
    ...(description ? { description } : {}),
    ...(chartFamily ? { chartFamily } : {}),
    ...(groupBy ? { groupBy } : {}),
    ...(groupMethod ? { groupMethod } : {}),
  };
}

function reconcileTableBuilderConfigs(raw: unknown): TableBuilderConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const c = reconcileTableBuilderConfig(item);
    return c ? [c] : [];
  });
}

function reconcileTableBuilderConfig(raw: unknown): TableBuilderConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
  const name = typeof obj['name'] === 'string' && obj['name'] ? obj['name'] : null;
  const sourceKey =
    typeof obj['sourceKey'] === 'string' && obj['sourceKey'] ? obj['sourceKey'] : null;
  const mode = obj['mode'] === 'aggregated' ? 'aggregated' : 'raw';
  if (!id || !name || !sourceKey) return null;
  const description =
    typeof obj['description'] === 'string' && obj['description'] ? obj['description'] : undefined;
  const base: TableBuilderConfig = {
    id,
    name,
    sourceKey,
    mode,
    ...(description ? { description } : {}),
  };
  if (mode === 'raw') {
    const columns = reconcileTableBuilderColumns(obj['columns']);
    const exportConfig = reconcileTableBuilderExportConfig(obj['exportConfig']);
    return { ...base, columns, ...(exportConfig ? { exportConfig } : {}) };
  }
  const dimensionField =
    typeof obj['dimensionField'] === 'string' && obj['dimensionField']
      ? obj['dimensionField']
      : undefined;
  const columnGroups = reconcileTableBuilderColumnGroups(obj['columnGroups']);
  const showFooterTotals =
    typeof obj['showFooterTotals'] === 'boolean' ? obj['showFooterTotals'] : false;
  return { ...base, ...(dimensionField ? { dimensionField } : {}), columnGroups, showFooterTotals };
}

function reconcileTableBuilderColumns(raw: unknown): TableBuilderColumnDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const obj = item as Record<string, unknown>;
    const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
    const field = typeof obj['field'] === 'string' && obj['field'] ? obj['field'] : null;
    const header = typeof obj['header'] === 'string' ? obj['header'] : null;
    if (!id || !field || header === null) return [];
    return [{ id, field, header }];
  });
}

function reconcileTableBuilderExportConfig(raw: unknown): TableBuilderExportConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const format: TableBuilderExportFormat =
    obj['format'] === 'csv' ? 'csv' : obj['format'] === 'txt' ? 'txt' : 'xlsx';
  const includeHeaders = typeof obj['includeHeaders'] === 'boolean' ? obj['includeHeaders'] : true;
  const delimiter =
    typeof obj['delimiter'] === 'string' && obj['delimiter'] ? obj['delimiter'] : undefined;
  return { format, includeHeaders, ...(delimiter ? { delimiter } : {}) };
}

function reconcileTableBuilderColumnGroups(raw: unknown): TableBuilderColumnGroup[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const obj = item as Record<string, unknown>;
    const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
    const label = typeof obj['label'] === 'string' ? obj['label'] : null;
    if (!id || label === null) return [];
    const measures = reconcileTableBuilderColumns(obj['measures']);
    return [{ id, label, measures }];
  });
}

export function migrateLegacyPreferences(storage: Storage): UserPreferences {
  const defaults = cloneUserPreferences(DEFAULT_USER_PREFERENCES);

  const legacyTheme = safeRead(storage, USER_PREFERENCES_LEGACY_KEYS.theme);
  // With no explicit legacy choice, following the OS is what the old prefers-dark sniff approximated.
  const theme: ThemeMode =
    legacyTheme === 'dark' || legacyTheme === 'light' ? legacyTheme : defaults.theme;

  for (const key of Object.values(USER_PREFERENCES_LEGACY_KEYS)) {
    try {
      storage.removeItem(key);
    } catch {}
  }

  return {
    theme,
    dashboardLayout: defaults.dashboardLayout,
    dashboardOrder: defaults.dashboardOrder,
    dateRange: defaults.dateRange,
    reports: [],
    tags: [],
    dashboardPresets: [],
    scope: defaults.scope,
    organizations: defaults.organizations,
    tableBuilderConfigs: [],
    chartBuilderConfigs: [],
    sidebarCollapsed: false,
    tableColumns: {},
  };
}

function reconcileReports(raw: unknown): PersistedReport[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const r = reconcilePersistedReport(item);
    return r ? [r] : [];
  });
}

function reconcilePersistedReport(raw: unknown): PersistedReport | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
  if (!id) return null;
  const name = typeof obj['name'] === 'string' ? obj['name'].trim() : '';
  if (!name) return null;
  const description = typeof obj['description'] === 'string' ? obj['description'] : '';
  const createdAt =
    typeof obj['createdAt'] === 'string' ? obj['createdAt'] : new Date().toISOString();
  const updatedAt =
    typeof obj['updatedAt'] === 'string' ? obj['updatedAt'] : new Date().toISOString();
  const createdBy = typeof obj['createdBy'] === 'string' ? obj['createdBy'] : '';
  const isPublic = typeof obj['isPublic'] === 'boolean' ? obj['isPublic'] : false;
  const isFavourite = typeof obj['isFavourite'] === 'boolean' ? obj['isFavourite'] : false;
  const type = obj['type'] === 'predefined' ? 'predefined' : 'custom';
  // Reports stored before the dashboard/report split have no format; they were all plain
  // reports, so that is the only safe default.
  const format = obj['format'] === 'dashboard' ? 'dashboard' : 'report';
  const tags = reconcileReportTags(obj['tags']);
  const widgets = reconcileReportWidgets(obj['widgets']);
  const tabs = reconcileReportTabs(obj['tabs']);
  return {
    id,
    name,
    description,
    type,
    format,
    createdAt,
    updatedAt,
    createdBy,
    isPublic,
    isFavourite,
    tags,
    widgets,
    tabs,
  };
}

function reconcileReportTabs(raw: unknown): ReportTab[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const tabs = raw.flatMap((t) => {
    const tab = reconcileReportTab(t);
    return tab ? [tab] : [];
  });
  return tabs.length > 0 ? tabs : undefined;
}

function reconcileReportTab(raw: unknown): ReportTab | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
  if (!id) return null;
  const label = typeof obj['label'] === 'string' && obj['label'] ? obj['label'] : 'Tab';
  const widgets = reconcileReportWidgets(obj['widgets']);
  return { id, label, widgets };
}

function reconcileReportTags(raw: unknown): ReportTag[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const tags = raw.flatMap((t) => {
    if (!t || typeof t !== 'object') return [];
    const obj = t as Record<string, unknown>;
    const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
    const name = typeof obj['name'] === 'string' ? obj['name'] : '';
    const color = typeof obj['color'] === 'string' ? obj['color'] : '#000000';
    const description = typeof obj['description'] === 'string' ? obj['description'] : null;
    return id ? [{ id, name, color, description: description || null }] : [];
  });
  return tags.length > 0 ? tags : undefined;
}

function reconcilePresets(raw: unknown): DashboardPreset[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const obj = item as Record<string, unknown>;
    const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
    if (!id) return [];
    const name = typeof obj['name'] === 'string' ? obj['name'].trim() : '';
    if (!name) return [];
    const description =
      typeof obj['description'] === 'string' && obj['description'] ? obj['description'] : undefined;
    const colSpan = clampInt(obj['colSpan'], 1, 12, 6);
    const rowSpan = clampInt(obj['rowSpan'], 1, 24, 5);
    const rawConfig = (obj['config'] ?? {}) as Record<string, unknown>;
    const config = reconcileCardFromItemsArray(rawConfig);
    if (!config) return [];
    const preset: DashboardPreset = { id, name, config, colSpan, rowSpan };
    if (description) return [{ ...preset, description }];
    return [preset];
  });
}

function reconcileTags(raw: unknown): ReportTag[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((t) => {
    if (!t || typeof t !== 'object') return [];
    const obj = t as Record<string, unknown>;
    const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
    if (!id) return [];
    const name = typeof obj['name'] === 'string' ? obj['name'].trim() : '';
    if (!name) return [];
    const color = typeof obj['color'] === 'string' ? obj['color'] : '#000000';
    const description = typeof obj['description'] === 'string' ? obj['description'] : null;
    return [{ id, name, color, description: description || null }];
  });
}

function reconcileCustomMetricDef(raw: unknown): DashboardCustomMetricDefinition | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const key = typeof obj['key'] === 'string' && obj['key'] ? obj['key'] : null;
  if (!key) return null;
  const label = typeof obj['label'] === 'string' ? obj['label'].trim() : '';
  if (!label) return null;
  const group = typeof obj['group'] === 'string' ? obj['group'] : 'Custom Metrics';
  const icon = typeof obj['icon'] === 'string' ? obj['icon'] : 'quiz';
  const value = typeof obj['value'] === 'string' ? obj['value'] : '—';
  const tone: GrStatCardTone = VALID_TONES.includes(obj['tone'] as GrStatCardTone)
    ? (obj['tone'] as GrStatCardTone)
    : 'neutral';
  const description = typeof obj['description'] === 'string' ? obj['description'] : '';
  const logoSrc = typeof obj['logoSrc'] === 'string' && obj['logoSrc'] ? obj['logoSrc'] : undefined;
  const detail = typeof obj['detail'] === 'string' && obj['detail'] ? obj['detail'] : undefined;
  const rawBadge = obj['badge'];
  let badge: DashboardCustomMetricDefinition['badge'];
  if (rawBadge && typeof rawBadge === 'object') {
    const b = rawBadge as Record<string, unknown>;
    const bVal = typeof b['value'] === 'string' ? b['value'] : null;
    const bDir = b['direction'];
    const bDirValid = bDir === 'up' || bDir === 'down' || bDir === 'neutral' ? bDir : null;
    if (bVal && bDirValid) badge = { value: bVal, direction: bDirValid };
  }
  return {
    key,
    label,
    group,
    icon,
    value,
    tone,
    description,
    ...(logoSrc ? { logoSrc } : {}),
    ...(badge ? { badge } : {}),
    ...(detail ? { detail } : {}),
  };
}

function reconcileReportWidgets(value: unknown): DashboardWidget[] {
  if (!Array.isArray(value)) return [];
  const result: DashboardWidget[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const widget = reconcileWidget(raw);
    if (!widget) continue;
    if (seen.has(widget.id)) continue;
    seen.add(widget.id);
    result.push(widget);
  }
  return result;
}

function reconcileScope(value: unknown): ScopePreference {
  if (!value || typeof value !== 'object') {
    return {
      ...DEFAULT_SCOPE,
      selection: { ...DEFAULT_SCOPE.selection },
      favoriteViewIds: [...DEFAULT_SCOPE.favoriteViewIds],
      favoriteTagIds: [...DEFAULT_SCOPE.favoriteTagIds],
      favoriteAccountIds: [...DEFAULT_SCOPE.favoriteAccountIds],
    };
  }
  const obj = value as Record<string, unknown>;
  const favoriteViewIds = reconcileStringArray(
    obj['favoriteViewIds'],
    DEFAULT_SCOPE.favoriteViewIds,
  );
  const favoriteTagIds = reconcileStringArray(obj['favoriteTagIds'], DEFAULT_SCOPE.favoriteTagIds);
  const favoriteAccountIds = reconcileStringArray(
    obj['favoriteAccountIds'],
    DEFAULT_SCOPE.favoriteAccountIds,
  );
  return {
    selection: reconcileSelection(obj['selection']),
    favoriteViewIds,
    favoriteTagIds,
    favoriteAccountIds,
  };
}

function reconcileOrganizations(value: unknown): OrganizationsPreference {
  if (!value || typeof value !== 'object') return { ...DEFAULT_ORGANIZATIONS };
  const obj = value as Record<string, unknown>;
  const activeId = typeof obj['activeId'] === 'string' && obj['activeId'] ? obj['activeId'] : null;
  const primaryId =
    typeof obj['primaryId'] === 'string' && obj['primaryId'] ? obj['primaryId'] : null;
  return { activeId, primaryId };
}

function reconcileSelection(value: unknown): ScopeSelection {
  if (!value || typeof value !== 'object') return { kind: 'all' };
  const obj = value as Record<string, unknown>;
  const kind = obj['kind'];
  const id = typeof obj['id'] === 'string' ? obj['id'] : '';
  if ((kind === 'view' || kind === 'tag' || kind === 'account') && id) {
    return { kind, id };
  }
  return { kind: 'all' };
}

function reconcileStringArray(
  value: unknown,
  fallback: ReadonlyArray<string>,
): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [...fallback];
  return value.filter((id): id is string => typeof id === 'string');
}

function reconcileDateRange(value: unknown): DateRangePreference {
  if (!value || typeof value !== 'object') return { ...DEFAULT_DATE_RANGE };
  const obj = value as Record<string, unknown>;
  const presetId =
    typeof obj['presetId'] === 'string' &&
    VALID_PRESET_IDS.includes(obj['presetId'] as DateRangePresetId)
      ? (obj['presetId'] as DateRangePresetId)
      : DEFAULT_DATE_RANGE.presetId;
  const fromIso =
    typeof obj['fromIso'] === 'string' && ISO_DATE_RE.test(obj['fromIso']) ? obj['fromIso'] : '';
  const toIso =
    typeof obj['toIso'] === 'string' && ISO_DATE_RE.test(obj['toIso']) ? obj['toIso'] : '';
  return { presetId, fromIso, toIso };
}

function cloneWidget(widget: DashboardWidget): DashboardWidget {
  return {
    ...widget,
    config: {
      ...widget.config,
      items: widget.config.items.map(cloneItem),
    },
  };
}

function cloneItem(item: DashboardCardItem): DashboardCardItem {
  if (item.kind === 'chart') {
    return { ...item, ...(item.display ? { display: { ...item.display } } : {}) };
  }
  if (item.kind === 'metric' && item.customDef) {
    const def = item.customDef;
    return { ...item, customDef: { ...def, ...(def.badge ? { badge: { ...def.badge } } : {}) } };
  }
  return { ...item };
}

function reconcileTheme(value: unknown): ThemeMode {
  return value === 'dark' || value === 'light' || value === 'system'
    ? value
    : DEFAULT_USER_PREFERENCES.theme;
}

function reconcileTableColumns(value: unknown): Record<string, ReadonlyArray<string>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Record<string, ReadonlyArray<string>> = {};
  for (const [key, hidden] of Object.entries(value as Record<string, unknown>)) {
    const fields = reconcileStringArray(hidden, []);
    if (fields.length) result[key] = [...fields];
  }
  return result;
}

function reconcileDashboardLayout(value: unknown): DashboardCanvasLayout {
  if (!Array.isArray(value)) return DEFAULT_DASHBOARD_LAYOUT.map(cloneWidget);

  const result: DashboardWidget[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const widget = reconcileWidget(raw);
    if (!widget) continue;
    if (seen.has(widget.id)) continue;
    seen.add(widget.id);
    result.push(widget);
  }
  return result;
}

function reconcileWidget(raw: unknown): DashboardWidget | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : null;
  if (!id) return null;

  const cfg = (obj['config'] ?? {}) as Record<string, unknown>;
  const kindRaw = obj['kind'];

  const config = reconcileCardConfig(kindRaw, cfg);
  if (!config) return null;

  const colSpan = clampInt(obj['colSpan'], 1, 12, 4);
  const rowSpan = clampInt(obj['rowSpan'], 1, 24, 2);

  return {
    id,
    kind: 'card',
    config,
    colSpan,
    rowSpan,
    ...readPosition(obj),
  };
}

function reconcileCardConfig(
  kindRaw: unknown,
  cfg: Record<string, unknown>,
): DashboardCardConfig | null {
  if (Array.isArray(cfg['items'])) {
    return reconcileCardFromItemsArray(cfg);
  }
  if (kindRaw === 'card') {
    return reconcileCardFromItemsArray(cfg);
  }
  if (kindRaw === 'metric') {
    return reconcileLegacyMetricCard(cfg);
  }
  if (kindRaw === 'text') {
    return reconcileLegacyTextCard(cfg);
  }
  if (kindRaw === 'chart' || Array.isArray(cfg['panes']) || typeof cfg['sourceKey'] === 'string') {
    return reconcileLegacyChartCard(cfg);
  }
  return null;
}

function reconcileCardFromItemsArray(cfg: Record<string, unknown>): DashboardCardConfig | null {
  const rawItems = cfg['items'];
  if (!Array.isArray(rawItems)) return null;
  const cols = readCoord(cfg['cols']);
  const items = reconcileItemList(rawItems, 6);
  if (items.length === 0) return null;
  const headingRaw = cfg['heading'];
  const heading = typeof headingRaw === 'string' ? headingRaw.trim() : '';
  const layoutRaw = cfg['layout'];
  const layout = isValidCardLayout(layoutRaw) ? layoutRaw : undefined;
  const presetId =
    typeof cfg['presetId'] === 'string' && cfg['presetId'] ? cfg['presetId'] : undefined;
  return {
    heading,
    items,
    ...(layout ? { layout } : {}),
    ...(cols !== undefined && cols >= 1 ? { cols } : {}),
    ...(presetId ? { presetId } : {}),
  };
}

function reconcileItemList(
  raw: ReadonlyArray<unknown>,
  maxItems?: number,
): ReadonlyArray<DashboardCardItem> {
  const out: DashboardCardItem[] = [];
  for (const r of raw) {
    const item = reconcileCardItem(r);
    if (item) out.push(item);
  }
  const limit = maxItems ?? 3;
  return ensureItemIds(out.length > limit ? out.slice(0, limit) : out);
}

function reconcileCardItem(raw: unknown): DashboardCardItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const kind = obj['kind'];
  const id = typeof obj['id'] === 'string' && obj['id'] ? obj['id'] : makeItemId();

  if (kind === 'metric') {
    const metricKey = typeof obj['metricKey'] === 'string' ? obj['metricKey'] : '';
    if (!metricKey) return null;
    const sourceFilter =
      typeof obj['sourceFilter'] === 'string' && obj['sourceFilter']
        ? obj['sourceFilter']
        : undefined;
    const customDef = reconcileCustomMetricDef(obj['customDef']);
    const item: DashboardMetricItem = {
      kind: 'metric',
      id,
      metricKey,
      ...(sourceFilter ? { sourceFilter } : {}),
      ...(customDef ? { customDef } : {}),
      ...readItemLayout(obj),
    };
    return item;
  }

  if (
    kind === 'chart' ||
    (typeof obj['sourceKey'] === 'string' && obj['sourceKey'] in DASHBOARD_CHART_SOURCE_INDEX)
  ) {
    const sourceKey = typeof obj['sourceKey'] === 'string' ? obj['sourceKey'] : '';
    if (!(sourceKey in DASHBOARD_CHART_SOURCE_INDEX)) return null;
    const source = DASHBOARD_CHART_SOURCE_INDEX[sourceKey as DashboardChartSourceKey];
    const rawType = obj['chartType'];
    const chartType =
      typeof rawType === 'string' &&
      VALID_CHART_TYPES.includes(rawType as DashboardChartType) &&
      source.supportedTypes.includes(rawType as DashboardChartType)
        ? (rawType as DashboardChartType)
        : source.defaultType;
    const headingRaw = obj['heading'];
    const heading =
      typeof headingRaw === 'string' && headingRaw.trim() ? headingRaw.trim() : undefined;
    const display = reconcileChartDisplay(obj['display'], sourceKey as DashboardChartSourceKey);
    const customConfig =
      sourceKey === 'custom' ? reconcileCustomChartConfig(obj['customConfig']) : undefined;
    const chartBuilderConfigId =
      sourceKey === 'custom' &&
      typeof obj['chartBuilderConfigId'] === 'string' &&
      obj['chartBuilderConfigId']
        ? obj['chartBuilderConfigId']
        : undefined;
    const item: DashboardChartItem = {
      kind: 'chart',
      id,
      sourceKey: sourceKey as DashboardChartSourceKey,
      chartType,
      ...(heading ? { heading } : {}),
      ...(display ? { display } : {}),
      ...(customConfig ? { customConfig } : {}),
      ...(chartBuilderConfigId ? { chartBuilderConfigId } : {}),
      ...readItemLayout(obj),
    };
    return item;
  }

  if (kind === 'table') {
    const tableKey = typeof obj['tableKey'] === 'string' && obj['tableKey'] ? obj['tableKey'] : '';
    if (!tableKey) return null;
    const headingRaw = obj['heading'];
    const heading =
      typeof headingRaw === 'string' && headingRaw.trim() ? headingRaw.trim() : undefined;
    const customColumns = Array.isArray(obj['customColumns'])
      ? (obj['customColumns'] as unknown[]).filter((c): c is string => typeof c === 'string')
      : undefined;
    const tableDataMode =
      obj['tableDataMode'] === 'aggregated' ? ('aggregated' as const) : undefined;
    const tableBuilderConfigId =
      typeof obj['tableBuilderConfigId'] === 'string' && obj['tableBuilderConfigId']
        ? obj['tableBuilderConfigId']
        : undefined;
    const item: DashboardTableItem = {
      kind: 'table',
      id,
      tableKey,
      ...(heading ? { heading } : {}),
      ...(customColumns?.length ? { customColumns } : {}),
      ...(tableDataMode ? { tableDataMode } : {}),
      ...(tableBuilderConfigId ? { tableBuilderConfigId } : {}),
      ...readItemLayout(obj),
    };
    return item;
  }

  if (kind === 'text' || typeof obj['body'] === 'string') {
    const headingRaw = obj['heading'];
    const heading =
      typeof headingRaw === 'string' && headingRaw.trim() ? headingRaw.trim() : undefined;
    const body = typeof obj['body'] === 'string' ? obj['body'] : '';
    const item: DashboardTextItem = {
      kind: 'text',
      id,
      ...(heading ? { heading } : {}),
      body,
      ...readItemLayout(obj),
    };
    return item;
  }

  return null;
}

function reconcileLegacyMetricCard(cfg: Record<string, unknown>): DashboardCardConfig | null {
  const metricKey = typeof cfg['metricKey'] === 'string' ? cfg['metricKey'] : '';
  if (!metricKey) return null;
  const item: DashboardMetricItem = {
    kind: 'metric',
    id: makeItemId(),
    metricKey,
  };
  return { heading: '', items: [item] };
}

function reconcileLegacyTextCard(cfg: Record<string, unknown>): DashboardCardConfig | null {
  const headingRaw = cfg['heading'];
  const heading = typeof headingRaw === 'string' ? headingRaw.trim() : '';
  const body = typeof cfg['body'] === 'string' ? cfg['body'] : '';
  const item: DashboardTextItem = {
    kind: 'text',
    id: makeItemId(),
    body,
  };
  return { heading, items: [item] };
}

function reconcileLegacyChartCard(cfg: Record<string, unknown>): DashboardCardConfig | null {
  const rawPanes = cfg['panes'];
  const itemsFromPanes = Array.isArray(rawPanes) ? reconcileItemList(rawPanes) : null;
  const items =
    itemsFromPanes && itemsFromPanes.length > 0 ? itemsFromPanes : legacyChartConfigToItems(cfg);
  if (items.length === 0) return null;

  const headingRaw = cfg['heading'];
  let heading = typeof headingRaw === 'string' ? headingRaw.trim() : '';
  if (!heading) {
    const first = items[0];
    if (first.kind === 'chart') {
      heading = DASHBOARD_CHART_SOURCE_INDEX[first.sourceKey].defaultHeading;
    }
  }
  const layoutRaw = cfg['layout'];
  const layout = isValidCardLayout(layoutRaw) ? layoutRaw : undefined;
  return { heading, items, ...(layout ? { layout } : {}) };
}

function legacyChartConfigToItems(cfg: Record<string, unknown>): ReadonlyArray<DashboardCardItem> {
  const sourceKey = typeof cfg['sourceKey'] === 'string' ? cfg['sourceKey'] : '';
  if (!(sourceKey in DASHBOARD_CHART_SOURCE_INDEX)) return [];
  const source = DASHBOARD_CHART_SOURCE_INDEX[sourceKey as DashboardChartSourceKey];
  const rawType = cfg['chartType'];
  const display = reconcileChartDisplay(cfg['display'], sourceKey as DashboardChartSourceKey);
  const baseDisplay = display ? { display } : {};

  if (rawType === 'combo') {
    const secondaryRaw = cfg['comboSecondaryType'] ?? cfg['comboTrendType'];
    const secondary: LegacyComboSecondaryType =
      typeof secondaryRaw === 'string' &&
      LEGACY_COMBO_SECONDARY.includes(secondaryRaw as LegacyComboSecondaryType)
        ? (secondaryRaw as LegacyComboSecondaryType)
        : 'line';
    return [
      {
        kind: 'chart',
        id: makeItemId(),
        sourceKey: sourceKey as DashboardChartSourceKey,
        chartType: 'donut',
        ...baseDisplay,
      },
      {
        kind: 'chart',
        id: makeItemId(),
        sourceKey: sourceKey as DashboardChartSourceKey,
        chartType: secondary,
        ...baseDisplay,
      },
    ];
  }

  const chartType =
    typeof rawType === 'string' &&
    VALID_CHART_TYPES.includes(rawType as DashboardChartType) &&
    source.supportedTypes.includes(rawType as DashboardChartType)
      ? (rawType as DashboardChartType)
      : source.defaultType;

  return [
    {
      kind: 'chart',
      id: makeItemId(),
      sourceKey: sourceKey as DashboardChartSourceKey,
      chartType,
      ...baseDisplay,
    },
  ];
}

const VALID_SERIES_TYPES = new Set(['bar', 'bar-stacked', 'line', 'area']);

function reconcileCustomChartConfig(raw: unknown): DashboardCustomChartConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const xAxis = typeof obj['xAxis'] === 'string' && obj['xAxis'] ? obj['xAxis'] : null;
  if (!xAxis) return null;

  const rawSeries = obj['series'];
  let series: ReadonlyArray<DashboardCustomChartSeriesDef> | null = null;

  if (Array.isArray(rawSeries)) {
    const validated = rawSeries.filter(
      (s): s is DashboardCustomChartSeriesDef =>
        !!s &&
        typeof s === 'object' &&
        typeof (s as Record<string, unknown>)['measure'] === 'string' &&
        VALID_SERIES_TYPES.has((s as Record<string, unknown>)['chartType'] as string),
    );
    if (validated.length) series = validated;
  }

  if (!series) return null;
  const groupBy = typeof obj['groupBy'] === 'string' && obj['groupBy'] ? obj['groupBy'] : undefined;
  const groupMethodRaw = obj['groupMethod'];
  const groupMethod =
    groupMethodRaw === 'bars' || groupMethodRaw === 'stacked' ? groupMethodRaw : undefined;
  return {
    xAxis,
    series,
    ...(groupBy ? { groupBy } : {}),
    ...(groupMethod ? { groupMethod } : {}),
  };
}

function reconcileChartDisplay(
  raw: unknown,
  sourceKey: DashboardChartSourceKey,
): DashboardChartDisplayOptions | null {
  if (!raw || typeof raw !== 'object') return null;
  const def = DASHBOARD_CHART_SOURCE_INDEX[sourceKey];
  const allowed = new Set(def.availableOptions);
  const obj = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  if (allowed.has('granularity')) {
    const v = obj['granularity'];
    if (typeof v === 'string' && VALID_GRANULARITY.includes(v as DashboardChartGranularity)) {
      out['granularity'] = v;
    }
  }
  if (allowed.has('legendPosition')) {
    const v = obj['legendPosition'];
    if (
      typeof v === 'string' &&
      VALID_LEGEND_POSITION.includes(v as DashboardChartLegendPosition)
    ) {
      out['legendPosition'] = v;
    }
  }
  if (allowed.has('showDataLabels')) {
    const v = obj['showDataLabels'];
    if (typeof v === 'boolean') out['showDataLabels'] = v;
  }
  if (allowed.has('yAxisMin')) {
    const v = toFiniteNumber(obj['yAxisMin']);
    if (v !== null) out['yAxisMin'] = v;
  }
  if (allowed.has('yAxisMax')) {
    const v = toFiniteNumber(obj['yAxisMax']);
    if (v !== null) out['yAxisMax'] = v;
  }
  if (allowed.has('topN')) {
    const v = toFiniteNumber(obj['topN']);
    if (v !== null && v >= 1) out['topN'] = Math.round(v);
  }
  if (allowed.has('sortOrder')) {
    const v = obj['sortOrder'];
    if (typeof v === 'string' && VALID_SORT_ORDER.includes(v as DashboardChartSortOrder)) {
      out['sortOrder'] = v;
    }
  }
  if (allowed.has('colorScheme')) {
    const v = obj['colorScheme'];
    if (typeof v === 'string' && VALID_COLOR_SCHEME.includes(v as DashboardChartColorScheme)) {
      out['colorScheme'] = v;
    }
  }
  if (allowed.has('compareToPrevious')) {
    const v = obj['compareToPrevious'];
    if (typeof v === 'boolean') out['compareToPrevious'] = v;
  }

  return Object.keys(out).length > 0 ? (out as DashboardChartDisplayOptions) : null;
}

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function readCoord(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : undefined;
}

function readItemLayout(obj: Record<string, unknown>): {
  x?: number;
  y?: number;
  colSpan?: number;
  rowSpan?: number;
} {
  const x = readCoord(obj['x']);
  const y = readCoord(obj['y']);
  const cs = readCoord(obj['colSpan']);
  const rs = readCoord(obj['rowSpan']);
  return {
    ...(x !== undefined ? { x } : {}),
    ...(y !== undefined ? { y } : {}),
    ...(cs !== undefined && cs >= 1 ? { colSpan: cs } : {}),
    ...(rs !== undefined && rs >= 1 ? { rowSpan: rs } : {}),
  };
}

function readPosition(obj: Record<string, unknown>): { x?: number; y?: number } {
  const x = readCoord(obj['x']);
  const y = readCoord(obj['y']);
  return {
    ...(x !== undefined ? { x } : {}),
    ...(y !== undefined ? { y } : {}),
  };
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(Math.round(n), min), max);
}

function safeRead(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}
